import {
    Box,
    Card,
    CardContent,
    FormControlLabel,
    Typography,
    RadioGroup,
    FormControl,
} from "@mui/material";
import ReactSvgIcon from "../../../../../../UIElements/ReactSvgIcon";
import { ReactComponent as DownloadIcon } from "../../../../../../../public/icons/DownloadIcon.svg";
import Textarea from "../../../../../../UIElements/Textarea";
import CustomRadio from "../../../../../../UIElements/CustomRadio";
import { Document } from "../../../../../../../types";
import { useState } from "react";

type InstructionDocumentViewProps = {
    document: Document;
    makeDocumentCompliant?: Function;
    makeDocumentNotCompliant?: Function;
    downloadDocument: Function;
    isReadOnly?: boolean;
};

export default function InstructionDocumentView({
    document,
    makeDocumentCompliant,
    makeDocumentNotCompliant,
    downloadDocument,
    isReadOnly = false,
}: InstructionDocumentViewProps) {
    const [selected, setSelected] = useState<boolean | null>(
        document.compliant
    );

    return (
        <Box
            sx={{
                display: "flex",
                width: "28.25rem",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "flex-start",
                gap: "0.5rem",
                "@media (max-width:1024px)": {
                    width: "100%",
                },
            }}
        >
            <Card
                sx={{
                    display: "flex",

                    padding: "1.5rem",
                    alignItems: "flex-start",
                    justifyContent: "flex-end",
                    gap: "2rem",
                    alignSelf: "stretch",
                    border: "2px solid  #E3E6E8",
                    borderRadius: "1rem",
                    background: "#FFF",
                    /* Ombre card */
                }}
            >
                <CardContent
                    sx={{
                        flex: "1 0 0",
                        padding: 0,
                        ":last-child": {
                            paddingBottom: 0,
                        },
                        display: "flex",
                        flexDirection: "column",
                        gap: "1.5rem",
                        justifyContent: "center",
                        alignItems: "flex-start",
                        width: "100%",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            height: "1.25rem",
                            justifyContent: "space-between",
                            alignItems: "center",
                            alignSelf: "stretch",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "1rem",
                            }}
                        >
                            <Typography
                                sx={{
                                    color: "#023",
                                    leadingTrim: "both",

                                    fontFamily: "Roboto",
                                    fontSize: "0.875rem",
                                    fontStyle: "normal",
                                    fontWeight: 400,
                                    lineHeight: "normal",
                                }}
                            >
                                {document.name}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                height: "2.5625rem",
                                alignItems: "center",
                                gap: "1rem",
                            }}
                        >
                            <ReactSvgIcon
                                sx={{
                                    cursor: "pointer",
                                }}
                                onClick={async () => {
                                    await downloadDocument();
                                }}
                                ReactIcon={DownloadIcon}
                                reactIconProps={{
                                    width: "1.5rem",
                                    height: "1.5rem",
                                }}
                            />
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            gap: "1.5rem",
                            alignSelf: "stretch",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: "0.75rem",
                                alignSelf: "stretch",
                            }}
                        >
                            <Typography
                                sx={{
                                    color: "#023",
                                    fontFamily: "Roboto",
                                    fontSize: "0.875rem",
                                    fontStyle: "normal",
                                    fontWeight: "500",
                                    lineHeight: "120%" /* 1.05rem */,
                                    alignSelf: "stretch",
                                }}
                            >
                                Le document est-il conforme ?
                            </Typography>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: "2rem",
                                }}
                            >
                                <FormControl>
                                    <RadioGroup
                                        value={selected}
                                        row
                                        aria-labelledby="demo-row-radio-buttons-group-label"
                                        name="row-radio-buttons-group"
                                        onChange={async (
                                            event: React.ChangeEvent<HTMLInputElement>
                                        ) => {
                                            const value =
                                                event.target.value === "true"
                                                    ? true
                                                    : event.target.value ===
                                                      "false"
                                                    ? false
                                                    : null;

                                            if (makeDocumentCompliant) {
                                                await makeDocumentCompliant(
                                                    document,
                                                    value
                                                );
                                                setSelected(value);
                                            }
                                        }}
                                    >
                                        <FormControlLabel
                                            value={true}
                                            control={
                                                <CustomRadio
                                                    disabled={isReadOnly}
                                                />
                                            }
                                            label="Oui"
                                        />
                                        <FormControlLabel
                                            value={false}
                                            control={
                                                <CustomRadio
                                                    disabled={isReadOnly}
                                                />
                                            }
                                            label="Non"
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </Box>
                        </Box>
                        {selected === false ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    gap: "0.5rem",
                                    alignSelf: "stretch",
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: "#023",
                                        fontFamily: "Roboto",
                                        fontSize: "0.875rem",
                                        fontStyle: "normal",
                                        fontWeight: "500",
                                        lineHeight: "160%",
                                    }}
                                >
                                    Commentaire
                                </Typography>
                                <Textarea
                                    disabled={isReadOnly}
                                    defaultValue={document?.comment ?? ""}
                                    onBlur={async (
                                        event: React.ChangeEvent<HTMLTextAreaElement>
                                    ) => {
                                        if (event.target.value) {
                                            if (
                                                makeDocumentNotCompliant &&
                                                event.target.value.trim()
                                            ) {
                                                await makeDocumentNotCompliant(
                                                    document,
                                                    event.target.value.trim()
                                                );
                                            }
                                        }
                                    }}
                                />
                            </Box>
                        ) : null}
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
}
